/** Automation routes (IA §2 Operate › Automation). Every view's state is in the URL (RULES §UI-3). */
import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { appRoute } from '@/app/appRoute';
import { can } from '@/lib/can';
import { meQuery } from '@/lib/me';
import { str } from '@/lib/search';

export interface FlowsSearch {
  q?: string;
  status?: 'on' | 'off';
  trigger?: string;
  sort?: string;
  selected?: string;
  /** "templates": the gallery to start from. */
  view?: 'templates';
  /** Gallery filter: the module that published a template. */
  module?: string;
}

export interface NewFlowSearch {
  template?: string;
  /** Key of an AI draft kept in this tab's session storage. */
  draft?: string;
  /** Copy of an existing flow. */
  from?: string;
  /** Start from an asset rule: trigger "an asset rule fires" for this rule. */
  rule?: string;
  step?: string;
}

export interface FlowSearch {
  tab?: 'design' | 'runs';
  step?: string;
}

export interface RunsSearch {
  flow?: string;
  status?: string;
  sort?: string;
  selected?: string;
}

export interface TasksSearch {
  status?: 'open' | 'done';
  kind?: 'task' | 'approval';
  assigned?: 'me' | 'others' | 'anyone';
  sort?: string;
  selected?: string;
}

export interface MessagesSearch {
  q?: string;
  category?: string;
  selected?: string;
}

export interface MessageSearch {
  lang?: 'en' | 'nl' | 'fr';
  channel?: string;
}

const pick = <T extends string>(v: unknown, allowed: readonly T[]): T | undefined => (allowed.includes(v as T) ? (v as T) : undefined);

/** /automation lands on the first section this person may open. */
export const automationIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation',
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (can(me, 'flows.flow.read')) throw redirect({ to: '/automation/flows' });
    if (can(me, 'flows.task.work')) throw redirect({ to: '/automation/tasks' });
    if (can(me, 'flows.run.read')) throw redirect({ to: '/automation/runs' });
    if (can(me, 'comms.template.read')) throw redirect({ to: '/automation/messages' });
    throw redirect({ to: '/automation/flows' });
  },
});

export const flowsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/flows',
  staticData: { permission: 'flows.flow.read' },
  validateSearch: (s: Record<string, unknown>): FlowsSearch => ({
    q: str(s.q),
    status: pick(s.status, ['on', 'off'] as const),
    trigger: str(s.trigger),
    sort: str(s.sort),
    selected: str(s.selected),
    view: pick(s.view, ['templates'] as const),
    module: str(s.module),
  }),
  component: lazyRouteComponent(() => import('./flows/FlowsPage')),
});

export const newFlowRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/flows/new',
  staticData: { permission: 'flows.flow.read' },
  validateSearch: (s: Record<string, unknown>): NewFlowSearch => ({
    template: str(s.template),
    draft: str(s.draft),
    from: str(s.from),
    rule: str(s.rule),
    step: str(s.step),
  }),
  component: lazyRouteComponent(() => import('./editor/NewFlowPage')),
});

export const flowRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/flows/$flowId',
  staticData: { permission: 'flows.flow.read' },
  validateSearch: (s: Record<string, unknown>): FlowSearch => ({ tab: pick(s.tab, ['design', 'runs'] as const), step: str(s.step) }),
  component: lazyRouteComponent(() => import('./editor/FlowPage')),
});

export const runsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/runs',
  staticData: { permission: 'flows.run.read' },
  validateSearch: (s: Record<string, unknown>): RunsSearch => ({ flow: str(s.flow), status: str(s.status), sort: str(s.sort), selected: str(s.selected) }),
  component: lazyRouteComponent(() => import('./runs/RunsPage')),
});

export const runRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/runs/$runId',
  staticData: { permission: 'flows.run.read' },
  component: lazyRouteComponent(() => import('./runs/RunPage')),
});

export const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/tasks',
  staticData: { permission: 'flows.task.work' },
  validateSearch: (s: Record<string, unknown>): TasksSearch => ({
    status: pick(s.status, ['open', 'done'] as const),
    kind: pick(s.kind, ['task', 'approval'] as const),
    assigned: pick(s.assigned, ['me', 'others', 'anyone'] as const),
    sort: str(s.sort),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./tasks/TasksPage')),
});

export const messagesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/messages',
  staticData: { permission: 'comms.template.read' },
  validateSearch: (s: Record<string, unknown>): MessagesSearch => ({ q: str(s.q), category: str(s.category), selected: str(s.selected) }),
  component: lazyRouteComponent(() => import('./messages/MessagesPage')),
});

export const messageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/messages/$key',
  staticData: { permission: 'comms.template.read' },
  validateSearch: (s: Record<string, unknown>): MessageSearch => ({ lang: pick(s.lang, ['en', 'nl', 'fr'] as const), channel: str(s.channel) }),
  component: lazyRouteComponent(() => import('./messages/MessagePage')),
});
